import { AppDataState, LanguageCopy, ProductLink } from "../types/domain";

function tukiosProductLinks(obituaryUrl: string): ProductLink[] {
  return [
    { type: "flowers", label: "Send Flowers", subtitle: "Order flowers for the family", url: `${obituaryUrl}/flowers` },
    { type: "cards", label: "Send a Card", subtitle: "Show your sympathy to the family", url: `${obituaryUrl}/cards` },
    { type: "gifts", label: "Plant Trees", subtitle: "In remembrance", url: `${obituaryUrl}/gifts` },
  ];
}

const now = "2026-03-21T16:20:00.000Z";

export const publicCopy: LanguageCopy[] = [
  {
    code: "en",
    label: "English",
    heading: "Honor a life beautifully, gather with clarity, and feel held at every step.",
    support:
      "WeHonor is designed for families, guests, and funeral teams to move through service logistics with less friction and more grace.",
    cta: "RSVP to attend",
    consent:
      "I would like compassionate follow-up from the funeral home, including future planning resources.",
  },
  {
    code: "es",
    label: "Español",
    heading:
      "Honramos una vida con belleza, claridad y acompanamiento en cada paso.",
    support:
      "WeHonor ayuda a familias, invitados y personal funerario a coordinar el servicio con menos friccion y mas cuidado.",
    cta: "Confirmar asistencia",
    consent:
      "Deseo recibir seguimiento compasivo de la funeraria, incluidos recursos para planificacion futura.",
  },
];

export function createSeedState(): AppDataState {
  return {
    publicLanguage: "en",
    onboardingComplete: false,
    notifications: [],
    memorials: [],
    events: [],
    attendees: [],
    rsvps: [],
    checkIns: [],
    consents: [],
    familyTokens: [],
  };
}

export function createPostImportSeedState(): AppDataState {
  return {
    publicLanguage: "en",
    onboardingComplete: true,
    notifications: [],
    funeralHome: {
      id: "fh-monarch",
      name: "Monarch Funeral Home & Cremation Services",
      websiteUrl: "https://www.monarchfuneralhome.com",
      ownerName: "Bill",
      email: "bill@monarchfuneralhome.com",
      phone: "(954) 972-7565",
      address: "2950 N State Rd 7, Margate, FL 33063",
      providerLabel: "Tukios + Batesville",
      providerConfidence: "high",
      monitoring: {
        enabled: true,
        intervalMinutes: 30,
        lastScanAt: "2026-03-27T14:00:00.000Z",
        newCasesDetected: 0,
        notifyBeforeMinutes: 1440,
      },
      status: "active",
      createdAt: "2026-03-27T14:00:00.000Z",
    },
    billing: {
      id: "billing-monarch",
      funeralHomeId: "fh-monarch",
      plan: "professional",
      status: "trial",
      trialEndsAt: "2026-04-27T00:00:00.000Z",
    },
    memorials: [
      {
        id: "mem-jennifer-miano",
        decedentName: "Dr. Jennifer Laura Miano",
        dob: "1979-05-31",
        dod: "2026-03-23",
        obituaryUrl: "https://www.monarchfuneralhome.com/obituary/jennifer-miano",
        photoUrl: "https://www.monarchfuneralhome.com/fh_live/16500/16565/images/obituaries/9578583_fbse.png",
        funeralHomeId: "fh-monarch",
        status: "published",
        familyContact: "Steve Miano",
        themeNote: "A loving anesthesiologist and world traveler whose compassion touched every life she encountered.",
        heroQuote: "A shooting star that passed through our lives shining so radiantly.",
        productLinks: tukiosProductLinks("https://www.monarchfuneralhome.com/obituary/jennifer-miano"),
        shortSlug: "jennifer",
        shortUrl: "https://whnr.co/jennifer",
        permanentUrl: "https://wehonor.com/monarch-funeral-home-cremation-services/dr-jennifer-laura-miano",
        shortLinkExpiresAt: "2026-04-27T14:00:00.000Z",
        syncStatus: "synced",
        lastSyncAt: "2026-03-27T14:02:00.000Z",
        syncCount: 1,
      },
      {
        id: "mem-jimmy-billisi",
        decedentName: "Giacomo 'Jimmy' Billisi",
        dob: "1937-10-28",
        dod: "2026-03-21",
        obituaryUrl: "https://www.monarchfuneralhome.com/obituary/jimmy-billisi",
        photoUrl: "https://www.monarchfuneralhome.com/fh_live/16500/16565/images/obituaries/9577025_fbse.jpeg",
        funeralHomeId: "fh-monarch",
        status: "published",
        familyContact: "Ann Billisi",
        themeNote: "Italian Navy veteran, beloved patriarch, and a man who brought warmth to every room.",
        heroQuote: "He brought warmth and joy to every gathering.",
        productLinks: tukiosProductLinks("https://www.monarchfuneralhome.com/obituary/jimmy-billisi"),
        shortSlug: "giacomo",
        shortUrl: "https://whnr.co/giacomo",
        permanentUrl: "https://wehonor.com/monarch-funeral-home-cremation-services/giacomo-jimmy-billisi",
        shortLinkExpiresAt: "2026-04-27T14:00:00.000Z",
        syncStatus: "synced",
        lastSyncAt: "2026-03-27T14:02:00.000Z",
        syncCount: 1,
      },
      {
        id: "mem-shane-douglas",
        decedentName: "Shane Daniel Douglas",
        dob: "1999-07-21",
        dod: "2026-03-18",
        obituaryUrl: "https://www.monarchfuneralhome.com/obituary/shane-douglas",
        photoUrl: "https://www.monarchfuneralhome.com/fh_live/16500/16565/images/obituaries/9575947_fbse.png",
        funeralHomeId: "fh-monarch",
        status: "published",
        familyContact: "Dianne Daniel",
        themeNote: "An automotive enthusiast with a resilient spirit and a heart full of kindness.",
        heroQuote: "A kind heart with an easygoing nature and genuine spirit.",
        productLinks: tukiosProductLinks("https://www.monarchfuneralhome.com/obituary/shane-douglas"),
        shortSlug: "shane",
        shortUrl: "https://whnr.co/shane",
        permanentUrl: "https://wehonor.com/monarch-funeral-home-cremation-services/shane-daniel-douglas",
        shortLinkExpiresAt: "2026-04-27T14:00:00.000Z",
        syncStatus: "synced",
        lastSyncAt: "2026-03-27T14:02:00.000Z",
        syncCount: 1,
      },
      {
        id: "mem-baso-newball",
        decedentName: "Basiliki 'Baso' Newball",
        dob: "1943-12-20",
        dod: "2026-03-17",
        obituaryUrl: "https://www.monarchfuneralhome.com/obituary/baso-newball",
        photoUrl: "https://www.monarchfuneralhome.com/fh_live/16500/16565/images/obituaries/9576051_fbse.jpeg",
        funeralHomeId: "fh-monarch",
        status: "published",
        familyContact: "Elena Newball",
        themeNote: "A legacy of love, laughter, and generosity. Her children and grandchildren were her world.",
        heroQuote: "Her children and grandchildren were her greatest pride.",
        productLinks: tukiosProductLinks("https://www.monarchfuneralhome.com/obituary/baso-newball"),
        shortSlug: "basiliki",
        shortUrl: "https://whnr.co/basiliki",
        permanentUrl: "https://wehonor.com/monarch-funeral-home-cremation-services/basiliki-baso-newball",
        shortLinkExpiresAt: "2026-04-27T14:00:00.000Z",
        syncStatus: "synced",
        lastSyncAt: "2026-03-27T14:02:00.000Z",
        syncCount: 1,
      },
    ],
    events: [
      {
        id: "evt-miano-visitation",
        memorialId: "mem-jennifer-miano",
        type: "visitation",
        title: "Visitation",
        startAt: "2026-03-28T19:00:00.000Z",
        endAt: "2026-03-28T21:00:00.000Z",
        venue: "Monarch Funeral Home & Cremation Services",
        address: "2950 N State Rd 7, Margate, FL 33063",
        visibility: "public",
        capacity: 200,
        status: "scheduled",
        rsvpEnabled: true,
        checkInEnabled: true,
        qrMode: "combined",
        confirmationHeadline: "We will hold space for you.",
        confirmationBody: "You are confirmed for the visitation. The family will be grateful for your presence.",
      },
      {
        id: "evt-billisi-visitation",
        memorialId: "mem-jimmy-billisi",
        type: "visitation",
        title: "Visitation",
        startAt: "2026-03-24T20:00:00.000Z",
        endAt: "2026-03-25T00:00:00.000Z",
        venue: "Monarch Funeral Home",
        address: "2950 N State Rd 7, Margate, FL 33063",
        visibility: "public",
        capacity: 250,
        status: "scheduled",
        rsvpEnabled: true,
        checkInEnabled: true,
        qrMode: "combined",
        confirmationHeadline: "We will hold space for you.",
        confirmationBody: "You are confirmed for the visitation.",
      },
      {
        id: "evt-billisi-prayer",
        memorialId: "mem-jimmy-billisi",
        type: "memorial_service",
        title: "Prayer Service",
        startAt: "2026-03-24T22:30:00.000Z",
        endAt: "2026-03-24T23:00:00.000Z",
        venue: "Monarch Funeral Home",
        address: "2950 N State Rd 7, Margate, FL 33063",
        visibility: "public",
        capacity: 250,
        status: "scheduled",
        rsvpEnabled: true,
        checkInEnabled: true,
        qrMode: "combined",
        confirmationHeadline: "Your presence honors Jimmy.",
        confirmationBody: "You are confirmed for the prayer service.",
      },
      {
        id: "evt-billisi-mass",
        memorialId: "mem-jimmy-billisi",
        type: "funeral",
        title: "Funeral Mass",
        startAt: "2026-03-25T15:00:00.000Z",
        endAt: "2026-03-25T16:00:00.000Z",
        venue: "St. Elizabeth Ann Seton Catholic Church",
        address: "1401 Coral Ridge Drive, Coral Springs, FL 33071",
        visibility: "public",
        capacity: 300,
        status: "scheduled",
        livestreamUrl: "https://www.hdezwebcast.com/show/jimmy-billisi",
        rsvpEnabled: true,
        checkInEnabled: true,
        qrMode: "combined",
        confirmationHeadline: "Thank you for honoring Jimmy.",
        confirmationBody: "Your RSVP is recorded. A livestream link is also available.",
      },
      {
        id: "evt-billisi-cryptside",
        memorialId: "mem-jimmy-billisi",
        type: "graveside",
        title: "Cryptside Service",
        startAt: "2026-03-25T17:00:00.000Z",
        endAt: "2026-03-25T17:30:00.000Z",
        venue: "Our Lady Queen of Heaven Cemetery",
        address: "1500 S. State Road 7, North Lauderdale, FL 33068",
        visibility: "public",
        capacity: 100,
        status: "scheduled",
        rsvpEnabled: true,
        checkInEnabled: false,
        qrMode: "rsvp",
        confirmationHeadline: "Your presence means the world.",
        confirmationBody: "You are confirmed for the cryptside service.",
      },
      {
        id: "evt-douglas-visitation",
        memorialId: "mem-shane-douglas",
        type: "visitation",
        title: "Visitation",
        startAt: "2026-03-29T20:00:00.000Z",
        endAt: "2026-03-29T22:00:00.000Z",
        venue: "Monarch Funeral Home & Cremation Services",
        address: "2950 N State Rd 7, Margate, FL 33063",
        visibility: "public",
        capacity: 200,
        status: "scheduled",
        rsvpEnabled: true,
        checkInEnabled: true,
        qrMode: "combined",
        confirmationHeadline: "We will hold space for you.",
        confirmationBody: "You are confirmed for the visitation.",
      },
      {
        id: "evt-douglas-funeral",
        memorialId: "mem-shane-douglas",
        type: "funeral",
        title: "Funeral Service",
        startAt: "2026-03-29T22:00:00.000Z",
        endAt: "2026-03-29T23:00:00.000Z",
        venue: "Monarch Funeral Home & Cremation Services",
        address: "2950 N State Rd 7, Margate, FL 33063",
        visibility: "public",
        capacity: 200,
        status: "scheduled",
        livestreamUrl: "https://www.hdezwebcast.com/show/shane-douglas",
        rsvpEnabled: true,
        checkInEnabled: true,
        qrMode: "combined",
        confirmationHeadline: "Thank you for honoring Shane.",
        confirmationBody: "Your RSVP is recorded. A livestream link is also available.",
      },
      {
        id: "evt-newball-visitation",
        memorialId: "mem-baso-newball",
        type: "visitation",
        title: "Visitation",
        startAt: "2026-03-22T17:00:00.000Z",
        endAt: "2026-03-22T18:00:00.000Z",
        venue: "Monarch Funeral Home & Cremation Services",
        address: "2950 N State Rd 7, Margate, FL 33063",
        visibility: "public",
        capacity: 200,
        status: "completed",
        rsvpEnabled: true,
        checkInEnabled: true,
        qrMode: "combined",
        confirmationHeadline: "We will hold space for you.",
        confirmationBody: "You are confirmed for the visitation.",
      },
      {
        id: "evt-newball-memorial",
        memorialId: "mem-baso-newball",
        type: "memorial_service",
        title: "Memorial Service",
        startAt: "2026-03-22T18:00:00.000Z",
        endAt: "2026-03-22T19:00:00.000Z",
        venue: "Monarch Funeral Home & Cremation Services",
        address: "2950 N State Rd 7, Margate, FL 33063",
        visibility: "public",
        capacity: 200,
        status: "completed",
        livestreamUrl: "https://www.hdezwebcast.com/show/baso-newball",
        rsvpEnabled: true,
        checkInEnabled: true,
        qrMode: "combined",
        confirmationHeadline: "Thank you for honoring Baso.",
        confirmationBody: "Your presence honors her memory.",
      },
      {
        id: "evt-newball-reception",
        memorialId: "mem-baso-newball",
        type: "reception",
        title: "Reception",
        startAt: "2026-03-22T19:00:00.000Z",
        endAt: "2026-03-22T21:00:00.000Z",
        venue: "Monarch Funeral Home & Cremation Services",
        address: "2950 N State Rd 7, Margate, FL 33063",
        visibility: "public",
        capacity: 200,
        status: "completed",
        rsvpEnabled: false,
        checkInEnabled: false,
        qrMode: "combined",
        confirmationHeadline: "You are welcome.",
        confirmationBody: "Join us for a reception celebrating Baso's life.",
      },
    ],
    attendees: [
      {
        id: "att-steve-miano",
        memorialId: "mem-jennifer-miano",
        name: "Steve Miano",
        email: "steve@example.com",
        phone: "(954) 555-0101",
        relationship: "Husband",
        source: "staff_added",
        createdAt: now,
      },
      {
        id: "att-ann-billisi",
        memorialId: "mem-jimmy-billisi",
        name: "Ann Billisi",
        email: "ann@example.com",
        phone: "(954) 555-0102",
        relationship: "Wife",
        source: "staff_added",
        createdAt: now,
      },
      {
        id: "att-rose-oshaughnessy",
        memorialId: "mem-jimmy-billisi",
        name: "Rose O'Shaughnessy",
        email: "rose@example.com",
        relationship: "Daughter",
        source: "public_rsvp",
        createdAt: now,
      },
      {
        id: "att-dianne-daniel",
        memorialId: "mem-shane-douglas",
        name: "Dianne Daniel",
        email: "dianne@example.com",
        phone: "(954) 555-0103",
        relationship: "Mother",
        source: "staff_added",
        createdAt: now,
      },
    ],
    rsvps: [
      {
        id: "rsvp-rose-mass",
        attendeeId: "att-rose-oshaughnessy",
        eventId: "evt-billisi-mass",
        responseStatus: "attending",
        partySize: 3,
        note: "Coming with family from Brooklyn.",
        submittedAt: now,
      },
    ],
    checkIns: [],
    familyTokens: [
      {
        id: "ft-jennifer",
        token: "miano-fam-jennifer-2026",
        memorialId: "mem-jennifer-miano",
        familyName: "The Miano Family",
        createdAt: "2026-03-27T14:02:00.000Z",
        expiresAt: "2026-06-27T14:02:00.000Z",
        createdBy: "staff",
      },
      {
        id: "ft-jimmy",
        token: "billisi-fam-jimmy-2026",
        memorialId: "mem-jimmy-billisi",
        familyName: "The Billisi Family",
        createdAt: "2026-03-27T14:02:00.000Z",
        expiresAt: "2026-06-27T14:02:00.000Z",
        createdBy: "staff",
      },
      {
        id: "ft-shane",
        token: "douglas-fam-shane-2026",
        memorialId: "mem-shane-douglas",
        familyName: "The Douglas Family",
        createdAt: "2026-03-27T14:02:00.000Z",
        expiresAt: "2026-06-27T14:02:00.000Z",
        createdBy: "staff",
      },
      {
        id: "ft-baso",
        token: "newball-fam-baso-2026",
        memorialId: "mem-baso-newball",
        familyName: "The Newball Family",
        createdAt: "2026-03-27T14:02:00.000Z",
        expiresAt: "2026-06-27T14:02:00.000Z",
        createdBy: "staff",
      },
    ],
    consents: [
      {
        id: "consent-rose",
        attendeeId: "att-rose-oshaughnessy",
        eventId: "evt-billisi-mass",
        consentType: "preneed",
        version: "2026-03",
        granted: true,
        grantedAt: now,
        source: "rsvp",
      },
    ],
  };
}
